// Import React helpers
import React, { useMemo, useState } from "react";

// Import component CSS
import "./DualRangeSlider.css";



// Export reusable dual range slider
export const DualRangeSlider = ({
    min,
    max,
    step = 1,
    minValue,
    maxValue,
    onChange,
    label,
    formatValue = (value) => value,
    disabled = false,
}) => {

    // Convert the minimum boundary into a number
    const numericMin = Number(min);

    // Convert the maximum boundary into a number
    const numericMax = Number(max);

    // Convert the selected minimum value into a number
    const numericMinValue = Number(minValue);

    // Convert the selected maximum value into a number
    const numericMaxValue = Number(maxValue);

    // Track which thumb the user most recently interacted with
    const [activeThumb, setActiveThumb] = useState(null);



    // Calculate the complete available range
    const totalRange = numericMax - numericMin;



    // Calculate the minimum thumb percentage
    const minPercentage = useMemo(() => {

        // Avoid dividing by zero when the range boundaries match
        if (totalRange <= 0) {

            // Return the beginning of the track
            return 0;

        }

        // Convert the selected minimum value into a percentage
        return ((numericMinValue - numericMin) / totalRange) * 100;

    }, [
        numericMin,
        numericMinValue,
        totalRange,
    ]);



    // Calculate the maximum thumb percentage
    const maxPercentage = useMemo(() => {

        // Avoid dividing by zero when the range boundaries match
        if (totalRange <= 0) {

            // Return the end of the track
            return 100;

        }

        // Convert the selected maximum value into a percentage
        return ((numericMaxValue - numericMin) / totalRange) * 100;

    }, [
        numericMin,
        numericMaxValue,
        totalRange,
    ]);



    // Handle changes to the minimum thumb
    const handleMinChange = (event) => {

        // Convert the selected input value into a number
        const nextMinValue = Number(event.target.value);

        // Prevent the minimum value from exceeding the maximum value
        const safeMinValue = Math.min(nextMinValue, numericMaxValue);

        // Send the updated range to the parent component
        onChange({
            minValue: safeMinValue,
            maxValue: numericMaxValue,
        });

    };



    // Handle changes to the maximum thumb
    const handleMaxChange = (event) => {

        // Convert the selected input value into a number
        const nextMaxValue = Number(event.target.value);

        // Prevent the maximum value from dropping below the minimum value
        const safeMaxValue = Math.max(nextMaxValue, numericMinValue);

        // Send the updated range to the parent component
        onChange({
            minValue: numericMinValue,
            maxValue: safeMaxValue,
        });

    };



    // Render the reusable slider
    return (

        <div
            className={[
                "dual-range-slider",
                disabled ? "dual-range-slider--disabled" : "",
            ]
                .filter(Boolean)
                .join(" ")}
        >

            {label && (

                <div className="dual-range-slider__header">

                    <span className="dual-range-slider__values">

                        <span className="dual-range-slider__value">

                            {formatValue(numericMinValue)}

                        </span>

                        <span
                            className="dual-range-slider__value-separator"
                            aria-hidden="true"
                        >

                            to

                        </span>

                        <span className="dual-range-slider__value">

                            {formatValue(numericMaxValue)}

                        </span>

                    </span>

                </div>

            )}

            <div className="dual-range-slider__control">

                <div
                    className="dual-range-slider__track"
                    aria-hidden="true"
                >

                    <div
                        className="dual-range-slider__track-fill"
                        style={{
                            left: `${minPercentage}%`,
                            right: `${100 - maxPercentage}%`,
                        }}
                    />

                </div>

                {/* 
                <input
                    type="range"
                    min={numericMin}
                    max={numericMax}
                    step={step}
                    value={numericMinValue}
                    onChange={handleMinChange}
                    disabled={disabled}
                    className="dual-range-slider__input dual-range-slider__input--min"
                    aria-label={`${label || "Range"} minimum`}
                    style={{
                        zIndex:
                            numericMinValue >= numericMaxValue - Number(step)
                                ? 4
                                : 3,
                    }}
                />

                <input
                    type="range"
                    min={numericMin}
                    max={numericMax}
                    step={step}
                    value={numericMaxValue}
                    onChange={handleMaxChange}
                    disabled={disabled}
                    className="dual-range-slider__input dual-range-slider__input--max"
                    aria-label={`${label || "Range"} maximum`}
                    style={{
                        zIndex: 3,
                    }}
                />
                */}


                <input
                    // Render a native range input
                    type="range"

                    // Set the lowest available value
                    min={numericMin}

                    // Set the highest available value
                    max={numericMax}

                    // Set the amount changed for each movement
                    step={step}

                    // Provide the currently selected minimum value
                    value={numericMinValue}

                    // Update the selected minimum value
                    onChange={handleMinChange}

                    // Mark the minimum thumb as active when pointer interaction begins
                    onPointerDown={() => {

                        // Store the minimum thumb as the active thumb
                        setActiveThumb("min");

                    }}

                    // Mark the minimum thumb as active when it receives keyboard focus
                    onFocus={() => {

                        // Store the minimum thumb as the active thumb
                        setActiveThumb("min");

                    }}

                    // Prevent interaction when the slider is disabled
                    disabled={disabled}

                    // Apply the minimum range input styles
                    className="dual-range-slider__input dual-range-slider__input--min"

                    // Provide an accessible description
                    aria-label={`${label || "Range"} minimum`}

                    // Control which overlapping thumb appears on top
                    style={{
                        // Put the minimum thumb on top when it is active
                        zIndex:
                            activeThumb === "min"
                                ? 5
                                : numericMinValue === numericMaxValue &&
                                    numericMinValue > numericMin + totalRange / 2
                                    ? 4
                                    : 3,
                    }}
                />

                <input
                    // Render a native range input
                    type="range"

                    // Set the lowest available value
                    min={numericMin}

                    // Set the highest available value
                    max={numericMax}

                    // Set the amount changed for each movement
                    step={step}

                    // Provide the currently selected maximum value
                    value={numericMaxValue}

                    // Update the selected maximum value
                    onChange={handleMaxChange}

                    // Mark the maximum thumb as active when pointer interaction begins
                    onPointerDown={() => {

                        // Store the maximum thumb as the active thumb
                        setActiveThumb("max");

                    }}

                    // Mark the maximum thumb as active when it receives keyboard focus
                    onFocus={() => {

                        // Store the maximum thumb as the active thumb
                        setActiveThumb("max");

                    }}

                    // Prevent interaction when the slider is disabled
                    disabled={disabled}

                    // Apply the maximum range input styles
                    className="dual-range-slider__input dual-range-slider__input--max"

                    // Provide an accessible description
                    aria-label={`${label || "Range"} maximum`}

                    // Control which overlapping thumb appears on top
                    style={{
                        // Put the maximum thumb on top when it is active
                        zIndex:
                            activeThumb === "max"
                                ? 5
                                : numericMinValue === numericMaxValue &&
                                    numericMaxValue <= numericMin + totalRange / 2
                                    ? 4
                                    : 3,
                    }}
                />

            </div>

            <div
                className="dual-range-slider__boundaries"
                aria-hidden="true"
            >

                <span className="dual-range-slider__boundary">

                    {formatValue(numericMin)}

                </span>

                <span className="dual-range-slider__boundary">

                    {formatValue(numericMax)}

                </span>

            </div>

        </div>

    );

};